import { Fragment, useEffect, useRef, type CSSProperties } from 'react'

// The stage track: where a journey is, where it has been, and what comes next.
//
// # WHAT EVERY TRACK SAYS
//
// Done stages carry a tick, the current stage ("you are here") carries the
// brand ring, upcoming stages are dashed. A blocked stage is outlined in
// critical and does not pulse, because nothing is moving there. A rejected
// stage is a red cross and the connectors after it fade, because the journey
// will not reach them.
//
// # WHY ONLY THE CHANGED STAGE MOVES
//
// On first draw the ticks draw in one after another and the current stage
// pulses three times, which is the picture of a journey arriving at where it
// is. After that, when a stage signs, only that stage and its outgoing
// connector animate: redrawing every tick each time somebody signs would make
// the whole track flicker to report one change. Each piece's delay is decided
// the first time that stage is seen in that state and then kept, so an
// unchanged stage never receives a new animation or a new delay and the
// browser has nothing to replay.
//
// A screen reader hears the change through the polite live region the caller
// fills with `announce`, and each stage names its own state in words.

export type StageState = 'done' | 'current' | 'upcoming' | 'blocked' | 'rejected'
export type Stage = { key: string; label: string; state: StageState; sub?: string; meta?: string }

const STATE_WORD: Record<StageState, string> = {
  done: 'done',
  current: 'current stage',
  upcoming: 'not started',
  blocked: 'blocked',
  rejected: 'rejected',
}

// Milliseconds. The tick takes 240, the connector 400, the halo 1600 three
// times; the stagger is 120 per stage.
const STAGGER = 120
const TICK = 240
const FILL = 400

type ConnKind = 'fill' | 'flow' | 'todo' | 'faded'

function connectorKind(stages: Stage[], i: number): ConnKind {
  if (stages.slice(0, i + 1).some((s) => s.state === 'rejected')) return 'faded'
  const s = stages[i]
  if (s?.state === 'done') return 'fill'
  if (s?.state === 'current') return 'flow'
  return 'todo'
}

export function Tick() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path className="kit-tick" pathLength={1} d="M3.5 8.5l3 3 6-6.5" />
    </svg>
  )
}

export function Cross() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path className="kit-tick" pathLength={1} d="M4.5 4.5l7 7M11.5 4.5l-7 7" />
    </svg>
  )
}

export function StageTrack({ stages, label, announce }: { stages: Stage[]; label: string; announce?: string }) {
  const mounted = useRef(false)
  const born = useRef(new Map<string, number>())
  useEffect(() => {
    mounted.current = true
  }, [])

  // The first time a delay is needed it is worked out and remembered; every
  // later render reads the remembered value. See the note at the top.
  const delay = (id: string, compute: () => number): number => {
    const known = born.current.get(id)
    if (known !== undefined) return known
    const v = compute()
    born.current.set(id, v)
    return v
  }

  const first = !mounted.current
  const nodeId = (s: Stage) => 'n|' + s.key + '|' + s.state
  const last = stages.length - 1
  const rejectedAt = stages.findIndex((s) => s.state === 'rejected')
  // Which stages are in a state this track has not drawn before, worked out
  // before any delay is remembered for this render.
  const fresh = stages.map((s) => !born.current.has(nodeId(s)))

  return (
    <div className="kit-track-wrap">
      <ol className="kit-track" aria-label={label}>
        {stages.map((s, i) => {
          const tickDelay = delay(nodeId(s), () => (first ? i * STAGGER : 0))
          // A stage that has just become current waits for the stage before it
          // to finish signing, so the eye follows one movement along the track.
          const prevJustSigned = !first && i > 0 && stages[i - 1]?.state === 'done' && fresh[i - 1] === true
          const haloDelay = delay('h|' + s.key + '|' + s.state, () =>
            first ? i * STAGGER + TICK + 200 : prevJustSigned ? TICK + FILL : 0,
          )
          const kind = i < last ? connectorKind(stages, i) : null
          const connDelay =
            kind === null
              ? 0
              : delay('c|' + s.key + '|' + kind, () => (first ? i * STAGGER + TICK - 80 : kind === 'flow' ? haloDelay : TICK))
          const after = rejectedAt >= 0 && i > rejectedAt
          const vars = { '--kit-tick-delay': tickDelay + 'ms', '--kit-halo-delay': haloDelay + 'ms' } as CSSProperties
          return (
            <li
              key={s.key}
              className={'kit-stage kit-' + s.state + (after ? ' kit-after-reject' : '')}
              aria-current={s.state === 'current' || s.state === 'blocked' ? 'step' : undefined}
              style={vars}
            >
              <span className="kit-node" aria-hidden="true">
                {s.state === 'done' ? (
                  <Tick key="tick" />
                ) : s.state === 'rejected' ? (
                  <Cross key="cross" />
                ) : s.state === 'blocked' ? (
                  <Fragment key="bang">!</Fragment>
                ) : (
                  <Fragment key="n">{i + 1}</Fragment>
                )}
                {s.state === 'current' && <span key={s.key + s.state} className="kit-halo" />}
              </span>
              <span className="kit-stage-label">
                {s.label}
                <span className="vh">, {STATE_WORD[s.state]}</span>
              </span>
              {s.sub && <span className="kit-stage-sub">{s.sub}</span>}
              {s.meta && <span className="kit-stage-meta">{s.meta}</span>}
              {kind !== null && (
                <span
                  key={'c-' + kind}
                  className={'kit-conn kit-conn-' + kind}
                  aria-hidden="true"
                  style={{ '--kit-conn-delay': connDelay + 'ms' } as CSSProperties}
                />
              )}
            </li>
          )
        })}
      </ol>
      <p className="vh" aria-live="polite">
        {announce ?? ''}
      </p>
    </div>
  )
}

// The same journey as dots and bars, for a table row or a card, where a full
// track would not fit. It is one image with one name, so a screen reader hears
// "Stage 4 of 6, with the DPO" and not twelve shapes.
export function MiniTrack({ states, label }: { states: StageState[]; label: string }) {
  return (
    <span className="kit-mini" role="img" aria-label={label}>
      {states.map((s, i) => (
        <Fragment key={i}>
          {i > 0 && <b className={'kit-mini-bar' + (states[i - 1] === 'done' ? ' kit-done' : '')} />}
          <i className={'kit-mini-dot kit-' + s} />
        </Fragment>
      ))}
    </span>
  )
}
